import {
  Box,
  Container,
  Flex,
  Heading,
  VStack,
  EmptyState,
  Table,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FiSearch } from "react-icons/fi";
import { TablesService } from "@/client/sdk.gen";
import AddTable from "@/components/Tables/AddTable";
import PendingItems from "@/components/Pending/PendingItems";
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination";
import { ProcessStateTag } from "@/components/ui/state-tag";
import { ActionsMenu } from "@/components/Common/ActionsMenu";

const PER_PAGE = 5;

interface TableManagementProps {
  currentPage: number;
  onPageChange: (details: { page: number }) => void;
  showTitle?: boolean;
  onTableRowClick?: (tableId: string) => void; // 테이블 행 클릭 콜백
}

function getTablesQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      TablesService.readTables({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
      }),
    queryKey: ["tables", { page }],
  };
}

const TableList = ({
  currentPage,
  onPageChange,
  showTitle = false,
  onTableRowClick,
}: TableManagementProps) => {
  const { data, isLoading, isPlaceholderData, error } = useQuery({
    ...getTablesQueryOptions({ page: currentPage }),
    placeholderData: (prevData) => prevData,
    staleTime: 1000 * 30, // 30초로 단축하여 더 빠른 업데이트
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  if (error) {
    return <PendingItems />;
  }

  const tables = data?.data ?? [];
  const count = data?.count ?? 0;

  if (isLoading) {
    return <PendingItems />;
  }

  const TableContent = () => {
    if (tables.length === 0) {
      return (
        <EmptyState.Root>
          <EmptyState.Content>
            <EmptyState.Indicator>
              <FiSearch />
            </EmptyState.Indicator>
            <VStack textAlign="center">
              <EmptyState.Title>아직 생성된 테이블이 없습니다</EmptyState.Title>
              <EmptyState.Description>
                새 테이블을 추가해 시작해보세요
              </EmptyState.Description>
            </VStack>
          </EmptyState.Content>
        </EmptyState.Root>
      );
    }

    return (
      <>
        <Table.Root size={{ base: "sm", md: "md" }}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Index</Table.ColumnHeader>
              <Table.ColumnHeader>테이블 명</Table.ColumnHeader>
              <Table.ColumnHeader>설명</Table.ColumnHeader>
              <Table.ColumnHeader>생성일</Table.ColumnHeader>
              <Table.ColumnHeader>상태</Table.ColumnHeader>
              <Table.ColumnHeader>작업</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tables.map((table) => (
              <Table.Row
                key={table.id}
                opacity={isPlaceholderData ? 0.5 : 1}
                onClick={() => onTableRowClick?.(table.id)}
                cursor={onTableRowClick ? "pointer" : "default"}
                _hover={onTableRowClick ? { bg: "gray.50" } : {}}
              >
                <Table.Cell>{table.index}</Table.Cell>
                <Table.Cell>{table.table_name}</Table.Cell>
                <Table.Cell>{table.description || "N/A"}</Table.Cell>
                <Table.Cell>{table.created_at?.toLocaleString()}</Table.Cell>
                <Table.Cell>
                  <ProcessStateTag
                    state={table?.process_state || "IDLE"}
                    _disabled={{ opacity: 0.5 }}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Box onClick={(e) => e.stopPropagation()}>
                    {" "}
                    {/* 이벤트 전파 방지 */}
                    <ActionsMenu table={table} />
                  </Box>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        <Flex justifyContent="flex-end" mt={4}>
          <PaginationRoot
            count={count}
            pageSize={PER_PAGE}
            page={currentPage}
            onPageChange={onPageChange}
          >
            <Flex>
              <PaginationPrevTrigger />
              <PaginationItems />
              <PaginationNextTrigger />
            </Flex>
          </PaginationRoot>
        </Flex>
      </>
    );
  };

  const content = (
    <Box>
      <AddTable />
      <TableContent />
    </Box>
  );

  if (showTitle) {
    return (
      <Container maxW="full">
        <Heading size="lg" pt={12}>
          테이블 관리
        </Heading>
        {content}
      </Container>
    );
  }

  return content;
};

export default TableList;
